import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/AppLayout";
import {
  apiGetNavPermissions,
  apiUpdateNavPermissions,
  type NavVisibilitySetting,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Navigation, KeyRound, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/PageSkeleton";

const SECTION_ORDER: NavVisibilitySetting["section"][] = [
  "administration",
  "teamManagement",
];

export default function NavPermissionsSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ["nav-permissions"],
    queryFn: apiGetNavPermissions,
  });

  const [localPerms, setLocalPerms] = useState<NavVisibilitySetting[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalPerms(permissions);
    setHasChanges(false);
  }, [permissions]);

  const mutation = useMutation({
    mutationFn: (data: Omit<NavVisibilitySetting, "id">[]) =>
      apiUpdateNavPermissions(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["nav-permissions"] });
      toast({
        title: t("navPermissions.saveSuccess"),
        description: t("navPermissions.saveSuccessDesc"),
      });
      setHasChanges(false);
    },
    onError: () => {
      toast({
        title: t("navPermissions.saveError"),
        description: t("navPermissions.saveErrorDesc"),
        variant: "destructive",
      });
    },
  });

  type BoolKey =
    | "visibleToAdmin"
    | "visibleToHr"
    | "visibleToUser"
    | "visibleToLineManager"
    | "visibleToCLevel";

  const togglePerm = (
    navKey: string,
    section: string,
    column: BoolKey,
  ) => {
    setLocalPerms((prev) =>
      prev.map((p) =>
        p.navKey === navKey && p.section === section
          ? { ...p, [column]: !p[column] }
          : p,
      ),
    );
    setHasChanges(true);
  };

  const handleSave = () => {
    const payload = localPerms.map(
      ({ navKey, section, visibleToAdmin, visibleToHr, visibleToUser, visibleToLineManager, visibleToCLevel }) => ({
        navKey,
        section,
        visibleToAdmin,
        visibleToHr,
        visibleToUser,
        visibleToLineManager,
        visibleToCLevel,
      }),
    );
    mutation.mutate(payload);
  };

  const columns: { key: BoolKey; labelKey: string }[] = [
    { key: "visibleToAdmin",       labelKey: "fieldPermissions.admin" },
    { key: "visibleToHr",         labelKey: "fieldPermissions.hrManager" },
    { key: "visibleToUser",       labelKey: "navPermissions.user" },
    { key: "visibleToLineManager", labelKey: "fieldPermissions.lineManager" },
    { key: "visibleToCLevel",     labelKey: "navPermissions.cLevel" },
  ];

  const grouped = SECTION_ORDER.map((section) => ({
    section,
    items: localPerms.filter((p) => p.section === section),
  })).filter((g) => g.items.length > 0);

  return (
    <AppLayout>
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-slate-500/10 flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("navPermissions.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("navPermissions.subtitle")}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {mutation.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <Card key={i} className="shadow-card">
                <CardHeader className="pb-2">
                  <Skeleton className="h-5 w-32" />
                </CardHeader>
                <CardContent>
                  <TableSkeleton rows={4} cols={6} />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
        <>
        {grouped.map(({ section, items }) => (
          <Card key={section} className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                <Badge variant="secondary" className="text-xs">
                  {t(`nav.${section}`)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground w-1/4">
                        {t("navPermissions.navItem")}
                      </th>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          className="text-center py-2 px-2 font-medium text-muted-foreground"
                        >
                          {t(col.labelKey)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={`${item.navKey}-${item.section}`}
                        className="border-b last:border-0 hover:bg-muted/30"
                      >
                        <td className="py-2.5 px-2 font-medium text-foreground text-xs">
                          {t(`nav.${item.navKey}`, { defaultValue: item.navKey })}
                        </td>
                        {columns.map((col) => (
                          <td key={col.key} className="py-2.5 px-2 text-center">
                            <Checkbox
                              checked={item[col.key]}
                              onCheckedChange={() =>
                                togglePerm(item.navKey, item.section, col.key)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
        </>
        )}
      </div>
    </AppLayout>
  );
}
